import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    CardHeader,
    Button,
    Alert,
    CircularProgress,
    Breadcrumbs,
    IconButton,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink } from 'react-router-dom';
import { MemberProfile, MembershipTier } from '../../types';
import { memberProfileAPI, membershipTierAPI } from '../../services/api';

const ChangeMembershipPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [memberProfile, setMemberProfile] = useState<MemberProfile | null>(null);
    const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [success, setSuccess] = useState<string | null>(null);
    
    // New state for handling pending change conflict
    const [pendingChangeConflict, setPendingChangeConflict] = useState<any>(null);
    const [showCancelConfirmDialog, setShowCancelConfirmDialog] = useState<boolean>(false);

    useEffect(() => {
        if (id) {
            fetchData();
        }
    }, [id]);

    const fetchData = async () => {
        if (!id) return;

        setLoading(true);
        setError(null);

        try {
            // Fetch member profile and membership tiers in parallel
            const [profileResponse, tiersResponse] = await Promise.all([
                memberProfileAPI.getMemberProfileById(id),
                membershipTierAPI.getMembershipTiers(true) // Only active tiers
            ]);

            const profile = profileResponse.data;
            setMemberProfile(profile);

            // Sort tiers by displayOrder
            const sortedTiers = tiersResponse.data.sort((a: MembershipTier, b: MembershipTier) => 
                a.displayOrder - b.displayOrder
            );
            setMembershipTiers(sortedTiers);

            // Set current tier as initially selected
            setSelectedTierId(profile.membershipTierId);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load membership information. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const isRestrictedTier = (tierName: string): boolean => {
        const lowerName = tierName.toLowerCase();
        return lowerName.includes('junior') || lowerName.includes('out of town');
    };

    const handleTierSelect = (tierId: string, tierName: string) => {
        if (isRestrictedTier(tierName)) {
            return; // Don't allow selection of restricted tiers
        }
        setSelectedTierId(tierId);
    };

    const handleConfirmChange = () => {
        // Open confirmation dialog
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
    };

    const handleProceedWithChange = async () => {
        if (!memberProfile || !selectedTierId || !id) return;

        setIsProcessing(true);
        setError(null);

        const selectedTier = membershipTiers.find(tier => tier.id === selectedTierId);
        const currentTier = membershipTiers.find(tier => tier.id === memberProfile.membershipTierId);

        if (!selectedTier || !currentTier) {
            setError('Invalid membership tier selection');
            setIsProcessing(false);
            return;
        }

        const isUpgrade = selectedTier.monthlyPrice > currentTier.monthlyPrice;

        try {
            // Use the new unified requestMembershipChange API
            const response = await memberProfileAPI.requestMembershipChange(id, {
                membershipTierId: selectedTierId,
                membershipChangeType: isUpgrade ? 'upgrade' : 'downgrade',
                effectiveDate: new Date().toISOString()
            });

            // Close dialog immediately and show success message
            setConfirmDialogOpen(false);
            setSuccess(response.data.message || 
                (isUpgrade 
                    ? 'Membership upgrade request scheduled successfully!'
                    : 'Membership downgrade request scheduled successfully!')
            );
            
            // Scroll to top to show the success message
            window.scrollTo({ top: 0, behavior: 'smooth' });
            
            // Navigate back after a short delay
            setTimeout(() => {
                navigate(`/member-profile`);
            }, 3000);
        } catch (err: any) {
            console.error('Error changing membership:', err);
            
            // Check if this is a pending change conflict (409 status)
            if (err.response?.status === 409 && err.response?.data?.hasPendingChange) {
                // Store the conflict details and show the cancel confirmation dialog
                setPendingChangeConflict(err.response.data);
                setConfirmDialogOpen(false);
                setShowCancelConfirmDialog(true);
            } else {
                setError(err.response?.data?.message || 'Failed to change membership. Please try again.');
                setConfirmDialogOpen(false);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCancelExistingChange = async () => {
        if (!pendingChangeConflict || !id) return;

        setIsProcessing(true);
        setError(null);

        try {
            // Cancel the existing scheduled change
            await memberProfileAPI.cancelScheduledMembershipChange(
                id,
                pendingChangeConflict.existingChange.id
            );

            // Close the dialog
            setShowCancelConfirmDialog(false);
            setPendingChangeConflict(null);

            // Show success message
            setSuccess('Existing scheduled membership change has been cancelled. You can now request a new change.');
            
            // Scroll to top to show the success message
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err: any) {
            console.error('Error cancelling scheduled change:', err);
            setError(err.response?.data?.message || 'Failed to cancel existing change. Please try again.');
            setShowCancelConfirmDialog(false);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCloseCancelConfirmDialog = () => {
        setShowCancelConfirmDialog(false);
        setPendingChangeConflict(null);
    };

    const getConfirmationMessage = () => {
        if (!memberProfile || !selectedTierId) return '';

        const selectedTier = membershipTiers.find(tier => tier.id === selectedTierId);
        const currentTier = membershipTiers.find(tier => tier.id === memberProfile.membershipTierId);

        if (!selectedTier || !currentTier) return '';

        const isUpgrade = selectedTier.monthlyPrice > currentTier.monthlyPrice;

        // If member status is pending, show scheduled start date message
        if (memberProfile.status === 'pending') {
            const scheduledStartDate = memberProfile.startDate
                ? new Date(memberProfile.startDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                  })
                : 'N/A';
            return `The change in your membership will be effective on ${scheduledStartDate}.`;
        }

        // For active members
        if (isUpgrade) {
            return `The change in your membership will be effective immediately to ensure you can enjoy the benefits of ${selectedTier.name} membership. A prorated charge will appear on your account to cover the upgraded membership from today till the next billing date.`;
        } else {
            return `The change in your membership tier from ${currentTier.name} to ${selectedTier.name} will take place at the next billing cycle. Until then, you can continue to enjoy the benefits of your current membership.`;
        }
    };

    const formatCurrency = (amount: number | undefined) => {
        if (amount === undefined || amount === null) return 'N/A';
        return `$${Number(amount).toFixed(2)}`;
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error || !memberProfile) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || 'Member profile not found.'}
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(`/member-profiles/${id}`)}
                >
                    Back to Member Profile
                </Button>
            </Container>
        );
    }

    // Check if member status allows membership changes
    if (memberProfile.status !== 'active' && memberProfile.status !== 'pending') {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Your membership's current status does not allow you to change your membership tier. 
                    Please contact staff for assistance.
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(`/member-profile`)}
                >
                    Back to Member Profile
                </Button>
            </Container>
        );
    }

    const currentTier = membershipTiers.find(tier => tier.id === memberProfile.membershipTierId);
    const hasSelectedDifferentTier = selectedTierId && selectedTierId !== memberProfile.membershipTierId;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Success/Error Messages */}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Breadcrumbs */}
            {/* <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    className='user-link'
                    style={{ display: 'flex', alignItems: 'center' }}
                    color="inherit"
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    className='user-link'
                    style={{ display: 'flex', alignItems: 'center' }}
                    color="inherit"
                    to="/member-management"
                >
                    Member Management
                </RouterLink>
                <RouterLink
                    className='user-link'
                    style={{ display: 'flex', alignItems: 'center' }}
                    color="inherit"
                    to={`/member-profiles/${id}`}
                >
                    {memberProfile.firstName} {memberProfile.lastName}
                </RouterLink>
                <Typography color="text.primary">
                    Change Membership
                </Typography>
            </Breadcrumbs> */}

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* <IconButton onClick={() => navigate(`/member-profiles/${id}`)} sx={{ mr: 2 }}>
                        <ArrowBackIcon />
                    </IconButton> */}
                    <Box>
                        <Typography variant="h4" component="h1">
                            Change Membership
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {memberProfile.firstName} {memberProfile.lastName}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Current Membership Info */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
                <Typography variant="h6" gutterBottom>
                    Current Membership
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1">
                        <strong>{currentTier?.name || 'Unknown'}</strong>
                    </Typography>
                    <Chip 
                        label={formatCurrency(currentTier?.monthlyPrice) + '/month'} 
                        color="primary" 
                        size="small" 
                    />
                </Box>
                {currentTier?.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {currentTier.description}
                    </Typography>
                )}
            </Paper>

            {/* Membership Tiers Selection */}
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                Select New Membership Tier
            </Typography>

            <Grid container spacing={3}>
                {membershipTiers.map((tier) => {
                    const isCurrentTier = tier.id === memberProfile.membershipTierId;
                    const isSelected = tier.id === selectedTierId;
                    const isRestricted = isRestrictedTier(tier.name);

                    return (
                        <Grid key={tier.id} size={{ xs: 12, md: 6, lg: 4 }}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    border: isSelected ? 2 : 1,
                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                    cursor: isRestricted ? 'not-allowed' : 'pointer',
                                    opacity: isRestricted ? 0.6 : 1,
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        borderColor: isRestricted ? 'divider' : 'primary.main',
                                        boxShadow: isRestricted ? 1 : 4,
                                    }
                                }}
                                onClick={() => handleTierSelect(tier.id, tier.name)}
                            >
                                {isCurrentTier && (
                                    <Chip
                                        label="Current Membership"
                                        color="success"
                                        size="small"
                                        icon={<CheckCircleIcon />}
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            right: 16,
                                        }}
                                    />
                                )}
                                
                                <CardHeader
                                    title={tier.name}
                                    titleTypographyProps={{ variant: 'h6' }}
                                    sx={{ pb: 1 }}
                                />
                                
                                <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="h4" component="div" color="primary">
                                            {formatCurrency(tier.monthlyPrice)}
                                            <Typography variant="caption" component="span" color="text.secondary">
                                                /month
                                            </Typography>
                                        </Typography>
                                        {tier.initiationFee > 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                Initiation Fee: {formatCurrency(tier.initiationFee)}
                                            </Typography>
                                        )}
                                    </Box>

                                    {tier.description && (
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            {tier.description}
                                        </Typography>
                                    )}

                                    <Divider sx={{ my: 2 }} />

                                    <List dense sx={{ py: 0 }}>
                                        {tier.unlimitedAmenityBookings ? (
                                            <ListItem sx={{ px: 0 }}>
                                                <ListItemText 
                                                    primary="Unlimited amenity bookings"
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                            </ListItem>
                                        ) : tier.amenityBookingsLimit ? (
                                            <ListItem sx={{ px: 0 }}>
                                                <ListItemText 
                                                    primary={`${tier.amenityBookingsLimit} amenity bookings/month`}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                            </ListItem>
                                        ) : null}

                                        {tier.unlimitedClassBookings ? (
                                            <ListItem sx={{ px: 0 }}>
                                                <ListItemText 
                                                    primary="Unlimited class bookings"
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                            </ListItem>
                                        ) : tier.classBookingsLimit ? (
                                            <ListItem sx={{ px: 0 }}>
                                                <ListItemText 
                                                    primary={`${tier.classBookingsLimit} class bookings/month`}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                            </ListItem>
                                        ) : null}

                                        {tier.features && tier.features.length > 0 && (
                                            tier.features.map((feature, index) => (
                                                <ListItem key={index} sx={{ px: 0 }}>
                                                    <ListItemText 
                                                        primary={feature}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                    />
                                                </ListItem>
                                            ))
                                        )}
                                    </List>

                                    {isRestricted && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            Please contact staff for this membership
                                        </Alert>
                                    )}

                                    {isSelected && !isCurrentTier && !isRestricted && (
                                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                                            <CheckCircleIcon color="primary" sx={{ fontSize: 40 }} />
                                            <Typography variant="body2" color="primary" fontWeight="bold">
                                                Selected
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                    variant="outlined"
                    onClick={() => navigate(`/member-profile`)}
                    disabled={isProcessing}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!hasSelectedDifferentTier || isProcessing}
                    onClick={handleConfirmChange}
                >
                    {isProcessing ? <CircularProgress size={24} /> : 'Confirm Change'}
                </Button>
            </Box>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleCloseConfirmDialog}
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown={isProcessing}
            >
                <DialogTitle>
                    Confirm Membership Change
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {getConfirmationMessage()}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseConfirmDialog} 
                        disabled={isProcessing}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleProceedWithChange} 
                        variant="contained" 
                        color="primary"
                        disabled={isProcessing}
                    >
                        {isProcessing ? <CircularProgress size={24} /> : 'Proceed'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Existing Pending Change Dialog */}
            <Dialog
                open={showCancelConfirmDialog}
                onClose={handleCloseCancelConfirmDialog}
                maxWidth="md"
                fullWidth
                disableEscapeKeyDown={isProcessing}
            >
                <DialogTitle>
                    Existing Pending Membership Change
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        You already have a pending membership change scheduled.
                    </Alert>
                    
                    {pendingChangeConflict && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Current Pending Change:
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    <Box sx={{ flex: '1 1 45%' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Change Type:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {pendingChangeConflict.existingChange.changeType.toUpperCase()}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: '1 1 45%' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Effective Date:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {new Date(pendingChangeConflict.existingChange.effectiveDate).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: '1 1 45%' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            From:
                                        </Typography>
                                        <Typography variant="body1">
                                            {pendingChangeConflict.existingChange.currentTier}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ${pendingChangeConflict.existingChange.currentMonthlyPrice}/month
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: '1 1 45%' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            To:
                                        </Typography>
                                        <Typography variant="body1">
                                            {pendingChangeConflict.existingChange.requestedTier}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ${pendingChangeConflict.existingChange.requestedMonthlyPrice}/month
                                        </Typography>
                                    </Box>
                                </Box>

                                {pendingChangeConflict.associatedTransactions && 
                                 pendingChangeConflict.associatedTransactions.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Pending Transaction(s):
                                        </Typography>
                                        {pendingChangeConflict.associatedTransactions.map((tx: any, idx: number) => (
                                            <Box key={idx} sx={{ mt: 1 }}>
                                                <Typography variant="body2">
                                                    {tx.description}
                                                </Typography>
                                                <Typography variant="body2" fontWeight="bold">
                                                    Amount: ${tx.totalAmount.toFixed(2)} ({tx.status})
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Paper>

                            <DialogContentText>
                                To proceed with a new membership change request, you must first cancel 
                                the existing pending change. This will also cancel any associated pending transactions.
                            </DialogContentText>
                            
                            <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
                                Do you want to cancel the existing pending change?
                            </DialogContentText>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseCancelConfirmDialog} 
                        disabled={isProcessing}
                    >
                        Keep Existing Change
                    </Button>
                    <Button 
                        onClick={handleCancelExistingChange} 
                        variant="contained" 
                        color="warning"
                        disabled={isProcessing}
                    >
                        {isProcessing ? <CircularProgress size={24} /> : 'Cancel Existing & Proceed'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ChangeMembershipPage;
